import { MigrationReview } from '@/components/MigrationReview';
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAccountDatabases,
  useCreateAccountDatabase,
  useUpdateAccountDatabase,
  useDeleteAccountDatabase,
  useTestAccountDatabase,
  useTestInlineConnection,
  useSite,
  useAuthStore,
  useMigrationPreflight,
  siteKeys,
  sitesApi,
  isTauri,
} from "@wordpuppi/shared";
import type {
  CreateDatabaseConnectionRequest,
  UpdateDatabaseConnectionRequest,
  DatabaseConnection,
} from "@wordpuppi/shared";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/dialog";
import { saveBlob } from "@/utils/desktopActions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { LoadingSpinner } from "@/components/LoadingSkeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/sites/$siteSlug/settings/databases")({
  component: SiteDatabasesPage,
});

/**
 * Where the site's content lives RIGHT NOW. The connections list below only
 * shows external databases the user added — without this card a site on the
 * default hosted DB reads as "no databases" while its posts obviously exist
 * somewhere, which is exactly the confusion it answers.
 */
function ContentDatabaseCard() {
  const { siteSlug } = Route.useParams();
  const { data: siteData } = useSite(siteSlug);
  const site = siteData?.data;
  if (!site) return null;

  const home = site.detached_at
    ? {
        label: "Detached — SQLite export available",
        detail: `Hosting was disabled on ${new Date(site.detached_at).toLocaleString()}. Download the export below. Nothing edits this file offline yet.`,
      }
    : site.db_type === "sqlite"
      ? {
          label: "SQLite database on the hosting server",
          detail:
            "Hosted editing uses this database. To use your own server, add an external connection and migrate. Detaching instead disables hosting and provides a SQLite download.",
        }
      : {
          label: `External ${site.db_type === "postgres" ? "PostgreSQL" : "MySQL"} database`,
          detail: "Content lives in your own connected database.",
        };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Content Database</CardTitle>
        <CardDescription>
          Where this site's content lives right now
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="font-medium">{home.label}</div>
        <p className="text-sm text-muted-foreground mt-1">{home.detail}</p>
      </CardContent>
    </Card>
  );
}

/**
 * Detach: POST /detach repoints the site's content store at a server-side
 * verified SQLite export and disables hosted serving and editing. Source
 * content rows are removed after successful cutover. The server keeps the
 * export available for repeated downloads.
 *
 * Desktop (isTauri) saves that copy into the platform app-data dir — the
 * OS-blessed place for app data (~/Library/Application Support/
 * com.wordpuppi.desktop/sites/ on macOS) — and shows the full path. The browser
 * downloads it through `saveBlob`'s anchor branch. Neither one is an offline
 * editor; the file is for SQLite tools or your own WordPuppi server.
 */
function DetachToMachineCard() {
  const { siteSlug } = Route.useParams();
  const queryClient = useQueryClient();
  const { data: siteData } = useSite(siteSlug);
  const [busy, setBusy] = useState(false);
  const [savedPath, setSavedPath] = useState<string | null>(null);
  const actor = useAuthStore((state) => state.user?.pid ?? '');
  const [retire, setRetire] = useState(false);
  const [typedSlug, setTypedSlug] = useState('');
  const operation = siteData?.data?.detached_at ? 'export' : retire ? 'retire_and_detach' : 'detach';
  const preflight = useMigrationPreflight(siteSlug, { operation }, actor, true, siteData?.data?.account_pid ?? '');
  const review = preflight.data?.data;
  const actionContext = JSON.stringify([actor, siteSlug, siteData?.data?.account_pid, retire]);
  const currentContext = useRef(actionContext); currentContext.current = actionContext;
  const [maintenanceAck, setMaintenanceAck] = useState(false);
  const [writersAck, setWritersAck] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const inFlight = useRef(false), alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, [siteSlug]);

  const site = siteData?.data;
  if (!site) return null;

  /** @returns what to show the user: a full path on desktop, the file name on web. */
  const saveExport = async (): Promise<string> => {
    const context = actionContext;
    const checked = await sitesApi.migrationPreflight(siteSlug, { operation: 'export' });
    if (!alive.current || currentContext.current !== context || !checked.success || !checked.data?.can_execute || !checked.data.permissions.export) throw new Error('The server could not authorize this content export.');
    const bytes = await sitesApi.downloadDetachedDb(siteSlug);
    if (!alive.current || currentContext.current !== context) throw new Error('Export session changed.');
    const filename = `${siteSlug}-${Date.now()}.sqlite`;
    if (!isTauri()) {
      // Cast: TS types Uint8Array as ArrayBufferLike-backed, BlobPart wants ArrayBuffer.
      await saveBlob(new Blob([bytes as BlobPart]), filename);
      return filename;
    }
    const { appDataDir, join } = await import("@tauri-apps/api/path");
    const { mkdir, writeFile, BaseDirectory } = await import(
      "@tauri-apps/plugin-fs"
    );
    await mkdir("sites", { baseDir: BaseDirectory.AppData, recursive: true });
    await writeFile(`sites/${filename}`, bytes, {
      baseDir: BaseDirectory.AppData,
    });
    return join(await appDataDir(), "sites", filename);
  };

  const handleDetach = async () => {
    const context = actionContext;
    if (retire && (typedSlug !== siteSlug || !review?.review_fingerprint)) return;
    if (inFlight.current || uncertain || !review?.can_execute || !review.permissions.detach || !maintenanceAck || (review.maintenance.external_writers_ack_required !== false && !writersAck)) return;
    if (
      !(await confirmDialog(
        `Detach "${site.name}" and download its content database?\n\n` +
          (retire ? "Existing CDN pages and embedded forms may stay visible, but submission intake and member login stop. Form, submission and member records remain in the export. " : "") +
          "This moves content into a server-side SQLite export, removes source content rows after successful cutover, and disables hosted serving and editing. You get a SQLite file, not an offline editor. " +
          "Use SQLite tools or a separately configured local WordPuppi server to work with it. Media files, theme assets, and account settings are not included.",
        { destructive: true, confirmLabel: 'Detach site' },
      ))
    )
      return;
    setBusy(true);
    try {
      if (inFlight.current || !alive.current || currentContext.current !== context) return;
      inFlight.current = true;
      const checked = await sitesApi.migrationPreflight(siteSlug, { operation: retire ? 'retire_and_detach' : 'detach' });
      if (!alive.current || currentContext.current !== context || !checked.success || !checked.data?.can_execute || !checked.data.permissions.detach) throw new Error('Detach is blocked by the current impact review.');
      if (retire && checked.data.review_fingerprint !== review.review_fingerprint) { await preflight.refetch(); throw new Error('The reviewed site changed. Refresh and confirm again.'); }
      setUncertain(true);
      const res = await sitesApi.detach(siteSlug, { maintenance_acknowledged: maintenanceAck, external_writers_quiesced: writersAck, ack_hosting_disabled: maintenanceAck, ...(retire ? { retire_live_services: true, typed_site_slug: typedSlug, reviewed_fingerprint: review.review_fingerprint } : {}) });
      if (!alive.current || currentContext.current !== context) return;
      if (!res.data?.success) {
        toast.error(res.data?.message ?? "Detach failed");
        return;
      }
      setUncertain(false);
      // Before saveExport, so a failed download still leaves the re-download
      // button (and the sites list's detached flag) correct.
      await queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteSlug) });
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() });
      const path = await saveExport();
      setSavedPath(path);
      toast.success(`Export saved: ${path}`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to export site data",
      );
    } finally {
      inFlight.current = false; setBusy(false);
    }
  };

  const handleReDownload = async () => {
    setBusy(true);
    try {
      const path = await saveExport();
      setSavedPath(path);
      toast.success(`Export saved: ${path}`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save the export",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Detach and Export</CardTitle>
        <CardDescription>
          {site.detached_at
            ? "Hosting is disabled. Download the verified SQLite export below; downloading again creates a new file without overwriting previous downloads."
            : "Download a SQLite content database and disable hosted serving and editing. This does not enable offline editing. Media files and theme assets are not included."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!site.detached_at && !retire ? <Button variant="outline" disabled={busy || !review?.permissions.detach} onClick={() => { setRetire(true); setMaintenanceAck(false); setWritersAck(false); }}>Review retiring live services and detaching</Button> : null}
        {!site.detached_at && retire ? <div className="space-y-2"><p className="text-sm">Existing pages and embedded forms may stay visible, but submission intake and member login stop. Stored form, submission and member records remain in the export.</p><label className="grid gap-1 text-sm">Type {siteSlug} to confirm this site<Input aria-label="Confirm site slug for retirement" value={typedSlug} onChange={(event) => setTypedSlug(event.target.value)} disabled={busy} autoComplete="off" /></label><Button variant="outline" disabled={busy} onClick={() => { setRetire(false); setTypedSlug(''); setMaintenanceAck(false); setWritersAck(false); }}>Keep hosting</Button></div> : null}
        <MigrationReview data={review} loading={preflight.isFetching} error={!!preflight.error || preflight.data?.success === false} refresh={() => { void preflight.refetch(); void queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteSlug) }); }} />
        <p className="text-sm">A content export is not a complete backup. Credentials, trusted queued-work authority, recovery records and configuration are excluded along with media and theme bytes.</p>
        {uncertain ? <p role="alert">Detach was not confirmed. Refresh the impact review and site status; do not repeat the detach request.</p> : null}
        {!site.detached_at ? <><label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={maintenanceAck} onChange={(event) => setMaintenanceAck(event.target.checked)} disabled={busy} />I understand hosted editing and submissions will stop.</label>{review?.maintenance.external_writers_ack_required !== false ? <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={writersAck} onChange={(event) => setWritersAck(event.target.checked)} disabled={busy} />External writers are paused.</label> : null}</> : null}
        {savedPath && (
          <div
            className="font-mono text-xs text-muted-foreground break-all"
            title={savedPath}
          >
            {savedPath}
          </div>
        )}
        {site.detached_at ? (
          <Button variant="secondary" onClick={handleReDownload} disabled={busy || !review?.can_execute || !review.permissions.export || !!preflight.error}>
            {busy ? "Saving..." : "Download the export again"}
          </Button>
        ) : (
          <Button variant="destructive" onClick={handleDetach} disabled={busy || (retire && (typedSlug !== siteSlug || !review?.review_fingerprint)) || uncertain || !review?.can_execute || !review.permissions.detach || !maintenanceAck || (review.maintenance.external_writers_ack_required !== false && !writersAck) || !!preflight.error || preflight.isFetching}>
            {busy ? "Preparing export..." : retire ? "Retire services and export SQLite" : "Detach and download SQLite"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

type DbType = "postgres" | "mysql" | "sqlite";
type ConnectionMode =
  | "supabase"
  | "connection_string"
  | "connection_string_ssh";

function SiteDatabasesPage() {
  const { siteSlug } = Route.useParams();
  const actor = useAuthStore((state) => state.user?.pid ?? '');
  const contextSite = useSite(siteSlug);
  const permission = useMigrationPreflight(siteSlug, { operation: 'migrate' }, actor, true, contextSite.data?.data?.account_pid ?? '');
  const canManage = permission.data?.data?.permissions.manage_destinations === true;
  const { data: databasesData, isLoading } = useAccountDatabases();
  const createDatabase = useCreateAccountDatabase();
  const updateDatabase = useUpdateAccountDatabase();
  const deleteDatabase = useDeleteAccountDatabase();
  const testDatabase = useTestAccountDatabase();
  const testInline = useTestInlineConnection();

  const [showModal, setShowModal] = useState(false);
  const [editingDb, setEditingDb] = useState<DatabaseConnection | null>(null);
  const [connectionTested, setConnectionTested] = useState(false);
  const [newDbName, setNewDbName] = useState("");
  const [newDbType, setNewDbType] = useState<DbType>("postgres");
  const [connectionMode, setConnectionMode] =
    useState<ConnectionMode>("supabase");
  const [connectionString, setConnectionString] = useState("");
  const [projectRef, setProjectRef] = useState("");
  const [password, setPassword] = useState("");
  const [region, setRegion] = useState("us-east-1");
  const [usePooler, setUsePooler] = useState(true);

  const [sshHost, setSshHost] = useState("");
  const [sshPort, setSshPort] = useState("22");
  const [sshUsername, setSshUsername] = useState("");
  const [sshPassword, setSshPassword] = useState("");

  const updateField =
    <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
    (value: T) => {
      setter(value);
      setConnectionTested(false);
    };

  const databases = databasesData?.data ?? [];

  const supabasePreviewUrl =
    connectionMode === "supabase" && projectRef
      ? `postgres://postgres.${projectRef}:****@${
          usePooler
            ? `aws-0-${region}.pooler.supabase.com:5432`
            : `db.${projectRef}.supabase.co:5432`
        }/postgres`
      : "";

  const isEditing = editingDb !== null;
  const isSaving = createDatabase.isPending || updateDatabase.isPending;

  const openAddModal = () => {
    resetForm();
    setEditingDb(null);
    setShowModal(true);
  };

  const openEditModal = (db: DatabaseConnection) => {
    if (db.db_type === "sqlite") return;
    setEditingDb(db);
    setNewDbName(db.name);
    setNewDbType(db.db_type);
    setConnectionMode(
      db.ssh_enabled ? "connection_string_ssh" : "connection_string",
    );
    setConnectionString("");
    setProjectRef("");
    setPassword("");
    setRegion("us-east-1");
    setUsePooler(true);
    setSshHost(db.ssh_host ?? "");
    setSshPort(String(db.ssh_port ?? 22));
    setSshUsername(db.ssh_username ?? "");
    setSshPassword("");
    setConnectionTested(false);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (newDbType === "sqlite") { toast.error("Use built-in SQLite on the hosting server instead of a saved file profile."); return; }
    if (!canManage) return;
    if (!newDbName.trim()) {
      toast.error("Please enter a name for this connection");
      return;
    }

    if (isEditing) {
      const data: UpdateDatabaseConnectionRequest = {};
      if (newDbName.trim() !== editingDb!.name) data.name = newDbName.trim();
      if (newDbType !== editingDb!.db_type) data.db_type = newDbType;

      if (connectionMode === "supabase") {
        if (projectRef) data.supabase_project_ref = projectRef;
        if (password) data.supabase_password = password;
        if (projectRef && password) data.supabase_region = region;
        if (projectRef && password) data.supabase_use_pooler = usePooler;
      } else if (connectionMode === "connection_string_ssh") {
        if (connectionString) data.connection_string = connectionString;
        data.ssh_enabled = true;
        data.ssh_host = sshHost;
        data.ssh_port = parseInt(sshPort, 10) || 22;
        data.ssh_username = sshUsername;
        data.ssh_password = sshPassword;
      } else {
        if (connectionString) data.connection_string = connectionString;
        if (editingDb!.ssh_enabled) data.ssh_enabled = false;
      }

      try {
        await updateDatabase.mutateAsync({ id: editingDb!.id, data });
        toast.success(`Database connection "${newDbName}" updated`);
        setShowModal(false);
        resetForm();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to update connection",
        );
      }
    } else {
      const data: CreateDatabaseConnectionRequest = {
        name: newDbName.trim(),
        db_type: newDbType,
      };

      if (connectionMode === "supabase") {
        data.supabase_project_ref = projectRef;
        data.supabase_password = password;
        data.supabase_region = region;
        data.supabase_use_pooler = usePooler;
      } else if (connectionMode === "connection_string_ssh") {
        data.connection_string = connectionString;
        data.ssh_enabled = true;
        data.ssh_host = sshHost;
        data.ssh_port = parseInt(sshPort, 10) || 22;
        data.ssh_username = sshUsername;
        data.ssh_password = sshPassword;
      } else {
        data.connection_string = connectionString;
      }

      try {
        await createDatabase.mutateAsync(data);
        toast.success(`Database connection "${newDbName}" created`);
        setShowModal(false);
        resetForm();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to create connection",
        );
      }
    }
  };

  const resetForm = () => {
    setNewDbType("postgres");
    setEditingDb(null);
    setNewDbName("");
    setProjectRef("");
    setPassword("");
    setConnectionString("");
    setSshHost("");
    setSshPort("22");
    setSshUsername("");
    setSshPassword("");
    setConnectionTested(false);
  };

  const buildTestData = (): CreateDatabaseConnectionRequest => {
    if (newDbType === "sqlite") throw new Error("Legacy SQLite profiles cannot be tested as new server connections.");
    const data: CreateDatabaseConnectionRequest = {
      name: newDbName.trim() || "test",
      db_type: newDbType,
    };

    if (connectionMode === "supabase") {
      data.supabase_project_ref = projectRef;
      data.supabase_password = password;
      data.supabase_region = region;
      data.supabase_use_pooler = usePooler;
    } else if (connectionMode === "connection_string_ssh") {
      data.connection_string = connectionString;
      data.ssh_enabled = true;
      data.ssh_host = sshHost;
      data.ssh_port = parseInt(sshPort, 10) || 22;
      data.ssh_username = sshUsername;
      data.ssh_password = sshPassword;
    } else {
      data.connection_string = connectionString;
    }

    return data;
  };

  const handleTestInline = async () => {
    const data = buildTestData();
    try {
      const result = await testInline.mutateAsync(data);
      if (result.data?.healthy) {
        toast.success("Connection successful!");
        setConnectionTested(true);
      } else {
        toast.error(result.data?.message || "Connection test failed");
        setConnectionTested(false);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Test failed");
      setConnectionTested(false);
    }
  };

  const handleDeleteDatabase = async (id: number, name: string) => {
    if (!(await confirmDialog(`Are you sure you want to delete "${name}"?`, { destructive: true, confirmLabel: 'Delete database' }))) return;
    try {
      await deleteDatabase.mutateAsync(id);
      toast.success("Database connection deleted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete");
    }
  };

  const handleTestDatabase = async (id: number) => {
    try {
      const result = await testDatabase.mutateAsync(id);
      if (result.data?.healthy) {
        toast.success("Connection successful!");
      } else {
        toast.error(result.data?.message || "Connection test failed");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Test failed");
    }
  };

  return (
    <div className="space-y-6">
      <ContentDatabaseCard />

      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>Database Connections</CardTitle>
              <CardDescription>
                External databases for storing site content
              </CardDescription>
            </div>
            <Button size="sm" onClick={openAddModal} disabled={!canManage}>
              + Add Database
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner />
          ) : databases.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">
                No database connections yet
              </p>
              <Button variant="secondary" onClick={openAddModal} disabled={!canManage}>
                Add Your First Database
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {databases.map((db) => (
                <div key={db.id} className="border rounded-lg p-3 space-y-2">
                  {db.db_type === "sqlite" ? <p className="text-sm text-muted-foreground">Legacy SQLite profile. Use built-in SQLite on the hosting server for sites and migrations. This saved record can be deleted; it is not a device database destination.</p> : null}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium truncate">{db.name}</span>
                      <span className="text-xs text-muted-foreground uppercase shrink-0">
                        {db.db_type}
                      </span>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!canManage || db.db_type === "sqlite"} onClick={() => openEditModal(db)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTestDatabase(db.id)}
                        disabled={!canManage || db.db_type === "sqlite" || testDatabase.isPending}
                      >
                        {testDatabase.isPending ? "Testing..." : "Test"}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDeleteDatabase(db.id, db.name)}
                        disabled={!canManage || deleteDatabase.isPending}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  <div
                    className="font-mono text-xs text-muted-foreground truncate"
                    title={db.connection_preview}
                  >
                    {db.connection_preview}
                  </div>
                  {db.ssh_enabled && (
                    <div className="text-xs text-muted-foreground">
                      SSH: {db.ssh_username}@{db.ssh_host}
                      {db.ssh_port !== 22 && `:${db.ssh_port}`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <DetachToMachineCard />

      <Dialog
        open={showModal}
        onOpenChange={(open) => !isSaving && setShowModal(open)}
      >
        <DialogContent className="max-w-md max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>
              {isEditing
                ? "Edit Database Connection"
                : "Add Database Connection"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Connection Name</Label>
              <Input
                type="text"
                value={newDbName}
                onChange={(e) => setNewDbName(e.target.value)}
                placeholder="e.g., Production Supabase"
              />
            </div>

            <div className="space-y-2">
              <Label>Database Type</Label>
              <Select
                value={newDbType}
                onValueChange={(v: string) =>
                  updateField(setNewDbType)(v as DbType)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="postgres">PostgreSQL</SelectItem>
                  <SelectItem value="mysql">MySQL</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Connection Mode</Label>
              <Select
                value={connectionMode}
                onValueChange={(v: string) =>
                  updateField(setConnectionMode)(v as ConnectionMode)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="supabase">Supabase</SelectItem>
                  <SelectItem value="connection_string">
                    Connection String
                  </SelectItem>
                  <SelectItem value="connection_string_ssh">
                    Connection String + SSH
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {connectionMode === "supabase" ? (
              <>
                <div className="space-y-2">
                  <Label>Project Reference</Label>
                  <Input
                    type="text"
                    value={projectRef}
                    onChange={(e) => updateField(setProjectRef)(e.target.value)}
                    placeholder="abcdefghijklmnop"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Database Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => updateField(setPassword)(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Region</Label>
                  <Select
                    value={region}
                    onValueChange={(v: string) => updateField(setRegion)(v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="us-east-1">
                        US East (N. Virginia)
                      </SelectItem>
                      <SelectItem value="us-west-1">
                        US West (N. California)
                      </SelectItem>
                      <SelectItem value="eu-west-1">EU (Ireland)</SelectItem>
                      <SelectItem value="eu-central-1">
                        EU (Frankfurt)
                      </SelectItem>
                      <SelectItem value="ap-southeast-1">
                        Asia Pacific (Singapore)
                      </SelectItem>
                      <SelectItem value="ap-northeast-1">
                        Asia Pacific (Tokyo)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Pooling Mode</Label>
                  <Select
                    value={usePooler ? "pooler" : "direct"}
                    onValueChange={(v: string) =>
                      updateField(setUsePooler)(v === "pooler")
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pooler">
                        Session Pooler (IPv4)
                      </SelectItem>
                      <SelectItem value="direct">Direct (IPv6)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {supabasePreviewUrl && (
                  <div className="bg-muted p-3 rounded-md text-sm font-mono break-all">
                    {supabasePreviewUrl}
                  </div>
                )}
              </>
            ) : connectionMode === "connection_string_ssh" ? (
              <>
                <div className="space-y-2">
                  <Label>Connection String</Label>
                  <Input
                    type="text"
                    value={connectionString}
                    onChange={(e) =>
                      updateField(setConnectionString)(e.target.value)
                    }
                    placeholder={
                      isEditing
                        ? "Leave blank to keep existing"
                        : "postgres://user:pass@host:5432/dbname"
                    }
                  />
                </div>
                <div className="bg-muted p-3 rounded-md space-y-3">
                  <div className="font-medium text-sm">SSH Tunnel</div>
                  <div className="space-y-2">
                    <Label>SSH Host</Label>
                    <Input
                      type="text"
                      value={sshHost}
                      onChange={(e) => updateField(setSshHost)(e.target.value)}
                      placeholder="server.example.com"
                    />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 space-y-2">
                      <Label>SSH Username</Label>
                      <Input
                        type="text"
                        value={sshUsername}
                        onChange={(e) =>
                          updateField(setSshUsername)(e.target.value)
                        }
                        placeholder="root"
                      />
                    </div>
                    <div className="w-20 space-y-2">
                      <Label>Port</Label>
                      <Input
                        type="text"
                        value={sshPort}
                        onChange={(e) =>
                          updateField(setSshPort)(e.target.value)
                        }
                        placeholder="22"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>SSH Password</Label>
                    <Input
                      type="password"
                      value={sshPassword}
                      onChange={(e) =>
                        updateField(setSshPassword)(e.target.value)
                      }
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label>Connection String</Label>
                <Input
                  type="text"
                  value={connectionString}
                  onChange={(e) =>
                    updateField(setConnectionString)(e.target.value)
                  }
                  placeholder={
                    isEditing
                      ? "Leave blank to keep existing"
                      : "postgres://user:pass@host:5432/dbname"
                  }
                />
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button
                variant="secondary"
                onClick={handleTestInline}
                disabled={testInline.isPending || isSaving}
              >
                {testInline.isPending ? "Testing..." : "Test Connection"}
              </Button>
              <Button
                onClick={handleSave}
                disabled={!canManage || !connectionTested || isSaving}
              >
                {isSaving
                  ? "Saving..."
                  : isEditing
                    ? "Update Connection"
                    : "Save Connection"}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
